import { useState } from 'react';
import { toast } from 'react-toastify';
import c from 'classnames';

import useUpdateBookmark from 'services/actionPlan/useUpdateBookmark';
import { BookmarkedItem, RecommendationItemType } from 'types/ActionPlan';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import type { EventPropertiesMap } from 'contexts/analytics/types';

import { Icon } from '@repo/ui';

import styles from './Bookmark.module.css';

type BookmarkProps = BookmarkedItem & {
  content_type?: RecommendationItemType;
  content_name?: string;
};

export const Bookmark = ({
  kit_ids,
  recommendation_item_id,
  is_bookmarked,
  action_plan_recommendation_id,
  content_type,
  content_name,
}: BookmarkProps) => {
  const [showBookmarked, setShowBookmarked] = useState(is_bookmarked);
  const updateBookmarkMutation = useUpdateBookmark();
  const { trackEvent } = useAnalytics();

  const toggleBookmark = async () => {
    const wasBookmarked = is_bookmarked;
    try {
      //toggle visually optimistically since the network request has a small delay
      setShowBookmarked(v => !v);
      await updateBookmarkMutation.mutateAsync({
        kit_ids,
        recommendation_item_id,
        action_plan_recommendation_id,
        is_bookmarked: !is_bookmarked,
      });
      const resolvedContentType = (content_type ??
        'article') as EventPropertiesMap['Bookmark Added']['content_type'];
      const resolvedContentName =
        content_name ?? String(recommendation_item_id);
      if (!wasBookmarked) {
        trackEvent('Bookmark Added', {
          content_type: resolvedContentType,
          content_name: resolvedContentName,
        });
      } else {
        trackEvent('Bookmark Removed', {
          content_type: resolvedContentType,
          content_name: resolvedContentName,
        });
      }
    } catch (error) {
      //revert the optimistic update
      setShowBookmarked(v => !v);
      toast.error('Your request could not be completed. Please try again');
    }
  };

  return (
    <div
      className={c(
        styles.bookmark,
        !showBookmarked ? styles.empty : styles.checked,
      )}
      onClick={toggleBookmark}
    >
      <Icon name={!showBookmarked ? 'bookmark' : 'bookmarkFilled'} />
    </div>
  );
};
